import { FieldInstructionBundle, FormValues } from 'containers/FormBuilder'
import * as fields from 'form/fields'
import { createZipFieldInstruction } from 'form/fields/addressFields'

import { gridColumnWrapper } from './gridColumnWrapper'
import {
    applicantFieldNames,
    coapplicantFieldNames,
    isApplicationJointFunc,
    isCoapplicantAddressSameFunc,
    otherFieldNames,
} from './sharedInstructions'
import { generateConditionallyValidatedFields } from './utils'
import {
    coapplicantWisconsinFieldNames,
    wisconsinFieldNames,
} from './wisconsinFieldNames'

export { wisconsinFieldNames, coapplicantWisconsinFieldNames }

export function isPrimaryWisconsinResidentFunc(values: FormValues) {
    return values[applicantFieldNames.state] === 'WI'
}

export function isCoapplicantWisconsinResidentFunc(values: FormValues) {
    const coapplicantStateFieldName = isCoapplicantAddressSameFunc(values)
        ? applicantFieldNames.state
        : coapplicantFieldNames.state

    return (
        isApplicationJointFunc(values) &&
        values[coapplicantStateFieldName] === 'WI'
    )
}

export function isSpouseCoapplicantFunc(values: FormValues) {
    return (
        isApplicationJointFunc(values) &&
        values[otherFieldNames.relationOfCoapplicant] === 'Spouse'
    )
}

export function isWisconsinStateFiveFunc(values: FormValues) {
    return (
        isSpouseCoapplicantFunc(values) &&
        (isPrimaryWisconsinResidentFunc(values) ||
            isCoapplicantWisconsinResidentFunc(values))
    )
}

export type WisconsinFieldNames = typeof wisconsinFieldNames

export function isMarriedFunc(fieldNames: WisconsinFieldNames) {
    return (values: FormValues) => values[fieldNames.maritalStatus] === 'M'
}

export function isSameAddressFunc(fieldNames: WisconsinFieldNames) {
    return (values: FormValues) =>
        values[fieldNames.isSpouseAddressSame] === true
}

type WisconsinFieldBundles = {
    maritalStatusFields: FieldInstructionBundle
    spouseInfoFields: FieldInstructionBundle
    spouseAddressFields: FieldInstructionBundle
}

export function createWisconsinFieldBundles(
    applicantLabel: 'Applicant' | 'Co-Applicant',
    fieldNames: WisconsinFieldNames,
    isSectionVisibleFunc: (values: FormValues) => boolean,
): WisconsinFieldBundles {
    const isMarried = isMarriedFunc(fieldNames)
    const isSameAddress = isSameAddressFunc(fieldNames)

    const isMarriedAndVisibleFunc = (values: FormValues) =>
        isSectionVisibleFunc(values) && isMarried(values)

    const isMarriedAndVisibleAndNotSameAddressFunc = (values: FormValues) =>
        isMarriedAndVisibleFunc(values) && !isSameAddress(values)

    const maritalStatusFields = generateConditionallyValidatedFields(
        {
            [fieldNames.maritalStatus]:
                fields.createWiMaritalStatusField(applicantLabel),
        },
        isSectionVisibleFunc,
    )

    const spouseInfoFields = generateConditionallyValidatedFields(
        {
            [fieldNames.spouseFirstName]: fields.wiSpouseFirstName,
            [fieldNames.spouseLastName]: fields.wiSpouseLastName,
            [fieldNames.isSpouseAddressSame]:
                fields.createWiSpouseAddressSameField(applicantLabel),
        },
        isMarriedAndVisibleFunc,
    )

    const spouseZipField = createZipFieldInstruction({
        city: fieldNames.spouseCity,
        state: fieldNames.spouseState,
    })

    const spouseAddressFields = generateConditionallyValidatedFields(
        {
            [fieldNames.spouseStreet]: gridColumnWrapper(
                fields.wiSpouseStreet,
                'span 8',
            ),
            [fieldNames.spouseApt]: gridColumnWrapper(
                fields.wiSpouseApt,
                'span 4',
            ),
            [fieldNames.spouseZip]: gridColumnWrapper(spouseZipField, 'span 4'),
            [fieldNames.spouseCity]: gridColumnWrapper(
                fields.wiSpouseCity,
                'span 4',
            ),
            [fieldNames.spouseState]: gridColumnWrapper(
                fields.wiSpouseState,
                'span 4',
            ),
        },
        isMarriedAndVisibleAndNotSameAddressFunc,
    )

    return {
        maritalStatusFields,
        spouseInfoFields,
        spouseAddressFields,
    }
}

export type WisconsinInstructionFields = {
    wisconsinApplicantMaritalStatusFields: FieldInstructionBundle
    wisconsinApplicantSpouseInfoFields: FieldInstructionBundle
    wisconsinApplicantSpouseAddressFields: FieldInstructionBundle
    wisconsinCoapplicantMaritalStatusFields: FieldInstructionBundle
    wisconsinCoapplicantSpouseInfoFields: FieldInstructionBundle
    wisconsinCoapplicantSpouseAddressFields: FieldInstructionBundle
}

export function createWisconsinInstructionFields(): WisconsinInstructionFields {
    const wisconsinApplicantFields = createWisconsinFieldBundles(
        'Applicant',
        wisconsinFieldNames,
        (values) =>
            isPrimaryWisconsinResidentFunc(values) &&
            !isSpouseCoapplicantFunc(values),
    )

    const wisconsinCoapplicantFields = createWisconsinFieldBundles(
        'Co-Applicant',
        coapplicantWisconsinFieldNames,
        (values) =>
            isCoapplicantWisconsinResidentFunc(values) &&
            !isSpouseCoapplicantFunc(values),
    )

    return {
        wisconsinApplicantMaritalStatusFields:
            wisconsinApplicantFields.maritalStatusFields,
        wisconsinApplicantSpouseInfoFields:
            wisconsinApplicantFields.spouseInfoFields,
        wisconsinApplicantSpouseAddressFields:
            wisconsinApplicantFields.spouseAddressFields,
        wisconsinCoapplicantMaritalStatusFields:
            wisconsinCoapplicantFields.maritalStatusFields,
        wisconsinCoapplicantSpouseInfoFields:
            wisconsinCoapplicantFields.spouseInfoFields,
        wisconsinCoapplicantSpouseAddressFields:
            wisconsinCoapplicantFields.spouseAddressFields,
    }
}

export type WisconsinSpouseData = {
    marital_status: string
    is_spouse_co_applicant?: boolean
    spouse_first_name?: string
    spouse_last_name?: string
    spouse_address?: string
    spouse_apt?: string
    spouse_city?: string
    spouse_state?: string
    spouse_zip?: string
}

type OwnAddress = {
    street: string
    apt: string
    city: string
    state: string
    zip: string
}

export function buildWisconsinSpouseData(
    values: FormValues,
    fieldNames: WisconsinFieldNames,
    isRequiredFunc: (values: FormValues) => boolean,
    isSpouseCoapplicant: boolean,
    ownAddress: OwnAddress,
): WisconsinSpouseData | undefined {
    if (!isRequiredFunc(values)) {
        return undefined
    }

    if (isSpouseCoapplicant) {
        return {
            marital_status: 'M',
            is_spouse_co_applicant: true,
        }
    }

    const maritalStatus = values[fieldNames.maritalStatus]

    if (maritalStatus !== 'M') {
        return { marital_status: maritalStatus }
    }

    const isSameAddress = values[fieldNames.isSpouseAddressSame] === true

    return {
        marital_status: maritalStatus,
        is_spouse_co_applicant: false,
        spouse_first_name: values[fieldNames.spouseFirstName],
        spouse_last_name: values[fieldNames.spouseLastName],
        spouse_address: isSameAddress
            ? ownAddress.street
            : values[fieldNames.spouseStreet],
        spouse_apt: isSameAddress
            ? ownAddress.apt
            : values[fieldNames.spouseApt],
        spouse_city: isSameAddress
            ? ownAddress.city
            : values[fieldNames.spouseCity],
        spouse_state: isSameAddress
            ? ownAddress.state
            : values[fieldNames.spouseState],
        spouse_zip: isSameAddress
            ? ownAddress.zip
            : values[fieldNames.spouseZip],
    }
}

export function buildPrimaryWisconsinSpouseData(
    values: FormValues,
): WisconsinSpouseData | undefined {
    return buildWisconsinSpouseData(
        values,
        wisconsinFieldNames,
        isPrimaryWisconsinResidentFunc,
        isSpouseCoapplicantFunc(values),
        {
            street: values[applicantFieldNames.street],
            apt: values[applicantFieldNames.apartmentSuite],
            city: values[applicantFieldNames.city],
            state: values[applicantFieldNames.state],
            zip: values[applicantFieldNames.zip],
        },
    )
}

export function buildCoapplicantWisconsinSpouseData(
    values: FormValues,
): WisconsinSpouseData | undefined {
    const ownAddressFieldNames = isCoapplicantAddressSameFunc(values)
        ? applicantFieldNames
        : coapplicantFieldNames

    return buildWisconsinSpouseData(
        values,
        coapplicantWisconsinFieldNames,
        isCoapplicantWisconsinResidentFunc,
        isSpouseCoapplicantFunc(values),
        {
            street: values[ownAddressFieldNames.street],
            apt: values[ownAddressFieldNames.apartmentSuite],
            city: values[ownAddressFieldNames.city],
            state: values[ownAddressFieldNames.state],
            zip: values[ownAddressFieldNames.zip],
        },
    )
}
